/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Logger } from '@app/core';
import { RolesService } from '@app/settings/users/roles/roles.service';
import { UserRole } from '@app/settings/users/roles/user-role.model';
import { PermissionsService } from '@app/settings/users/permissions.service';
import { ApiQuery } from '@app/core/http/api-query';
import { Permission } from '@app/settings/users/permissionsns.model';
import { of, Observable } from 'rxjs';

const log = new Logger('rolesFormComponent');

@Component({
  selector: 'app-roles-form',
  templateUrl: './roles-form.component.html',
  styleUrls: ['./roles-form.component.scss'],
})
export class RolesFormComponent implements OnInit {
  @Input() role: UserRole = null;
  form: FormGroup;
  permissions: Array<Permission>;
  loadedData = <any[]>[]; // вече запазените права за ролята, при нова роля не се използва

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private roleService: RolesService,
    private permissionService: PermissionsService
  ) {}

  ngOnInit() {
    this.createForm();
    this.loadData();
  }

  /**
   * Load permissions from API
   */
  loadData() {
    log.debug('loadData');
    this.getRoleObservable().subscribe((data: any) => {
      // Гетва правата за ролята. Функцията връща празен Observable ако ролята е нова
      if (data) {
        this.form.patchValue(this.role);
        this.loadedData = data.permissions.map((permission: any) => {
          return permission.id;
        });
      }

      /**
       *  Тук се взимат всички възможни права от апи-то
       */
      this.permissionService.index().subscribe((groups: any) => {
        Object.keys(groups).forEach((name) => {
          const permissions = groups[name];

          /**
           * Събираме всички Permission-и, създаваме  group(formBuilder.group) за всеки от тях и ги бутаме в масив
           */
          const permissionsControl = permissions.map((permission: Permission) => {
            return this.formBuilder.group({
              id: permission.id,
              name: permission.name,
              // Маркираме чекбокса ако конкретния permission е намерен в ролята. Ако ролята е нова undefined ще еволюира до false
              selected: this.loadedData.indexOf(permission.id) >= 0,
            });
          });

          /**
           * Събираме всички групи Permission-и и създаваме group(formBuilder.group) за тях
           */
          const permissionGroup = this.formBuilder.group({
            group: name,
            permissions: new FormArray(permissionsControl), // Създаваме масив(FormArray) със събраните permission-и по-горе
          });
          const groupArray: FormArray = <FormArray>this.form.controls.groups;
          groupArray.push(permissionGroup);
        });
      });
    });
  }

  save() {
    if (this.role) {
      const post: object = this.formatPost();
      post['id'] = this.role.id;
      this.roleService.update(post).subscribe((res) => this.modal.close('Save click'));
    } else {
      this.roleService.store(this.formatPost()).subscribe((res) => this.modal.close('Save click'));
    }
  }

  /**
   * mark all groups
   * @param bool mark or unmark
   */
  markAll(bool: boolean) {
    const groupsArray: FormArray = <FormArray>this.form.get('groups');
    groupsArray.controls.forEach((group: FormControl) => {
      const permissionsArray: FormArray = <FormArray>group.get('permissions');
      permissionsArray.controls.forEach((control: FormControl) => {
        control.patchValue({ selected: bool });
      });
    });
  }

  /**
   *  mark all from group
   * @param group witch group to mark
   * @param selected mark or unmark
   */
  markGroup(group: FormGroup, selected: boolean = true) {
    const permissionsArray: FormArray = <FormArray>group.get('permissions');
    permissionsArray.controls.forEach((control: FormControl) => {
      control.patchValue({ selected: selected });
    });
  }

  /**
   *  Създаваме инстанция на формата
   */
  private createForm() {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      groups: new FormArray([]),
    });
  }

  /**
   * helper function for format post request
   */
  private formatPost() {
    const post = {
      name: this.form.value.name,
      permissions: <any[]>[],
    };
    const groupsArray: FormArray = <FormArray>this.form.get('groups');
    groupsArray.controls.forEach((group: FormControl) => {
      const permissionsArray: FormArray = <FormArray>group.get('permissions');
      permissionsArray.controls.forEach((formGroup: FormGroup) => {
        if (formGroup.get('selected').value) {
          post.permissions.push({
            id: formGroup.get('id').value,
          });
        }
      });
    });
    return post;
  }

  private getRoleObservable(): Observable<any> {
    if (this.role) {
      return this.roleService.show(this.role.id, new ApiQuery().addInclude('permissions').setLimit(0));
    }
    return of(false);
  }
}
